import { runInAction } from "mobx";
import { dashboardService } from "../services/dashboard.service";
import { dashboardEChartsStore } from "../stores/e-charts.store";
import { dashboardEChartsTotalsStore } from "../stores/e-charts-totals.store";
import { dashboardFiltersStore } from "../stores/filters.store";
import { resolveMissingKpiYears } from "../utils/kpi-strip-years";
import {
  normalizeReportingPeriod,
  resolveReportingPeriodForYear,
  resolveSelectedReportingPeriod,
} from "../utils/reporting-years";

// Fingerprint of everything besides the year list that changes what a year's
// totals mean. Strip-fetched years are only merged into a summary that was
// produced under the same fingerprint. Order is preserved on purpose: the
// API uses the first framework, and period resolution picks the first record
// matching a start year, so reordering IS a semantic change.
export const buildTotalsContextKey = () => {
  const frameworks = [...(dashboardFiltersStore.filters.frameworks ?? [])].map(
    String,
  );
  const periods = (dashboardFiltersStore.available.reportingYears ?? [])
    .map(normalizeReportingPeriod)
    .filter(Boolean)
    .map((p) => `${p.startYear}.${p.startMonth}-${p.endYear}.${p.endMonth}`);
  return JSON.stringify({ frameworks, periods });
};

export const loadEChartsTotals = async () => {
  const store = dashboardEChartsTotalsStore;
  const { frameworks } = dashboardFiltersStore.filters;
  const years = [...dashboardEChartsStore.activeYears];
  const reportingPeriod = resolveSelectedReportingPeriod(
    years,
    dashboardFiltersStore.available.reportingYears,
  );
  const contextKey = buildTotalsContextKey();

  const seq = store.nextRequestSeq();
  store.setLoading(true);
  try {
    const data = await dashboardService.getEChartsTotals({
      frameworks,
      years,
      ...(reportingPeriod || {}),
    });
    runInAction(() => {
      if (store.isStaleSeq(seq)) return;
      // A response whose request-time context no longer matches the ambient
      // filters is stale even when its sequence is newest: the replacement
      // reaction is debounced, so an old-context response can land in that
      // window. Drop it; the pending reaction refetches under the new
      // context.
      if (contextKey !== buildTotalsContextKey()) return;
      store.setData(data, { contextKey, requestedYears: years });
    });
  } finally {
    if (!store.isStaleSeq(seq)) store.setLoading(false);
  }
};

// KPI-strip follow-up: fetch the selected year's totals when the range
// request does not cover it (a selected year outside the Start/End range
// used to render permanently empty cards). Each missing year is requested
// with its own boundary-resolved reporting period — the exact single-year
// request shape the API already serves — and merged under the loaded
// context, so a late response after a filter change is dropped instead of
// polluting the new context.
export const ensureKpiStripYears = async () => {
  const store = dashboardEChartsTotalsStore;

  // Only fetch while the loaded summary and the ambient filters agree. If
  // the filters changed but the new range response has not landed yet, bail:
  // fetching now would issue new-context params tagged with the old context.
  // The reaction refires when setData rotates the context key.
  const contextKey = buildTotalsContextKey();
  if (!store.hasLoadedOnce || contextKey !== store.contextKey) return;

  const missing = resolveMissingKpiYears({
    selectedYear: dashboardEChartsStore.selectedYear,
    hasLoadedOnce: store.hasLoadedOnce,
    loadedYears: store.loadedYears,
    pendingYears: store.pendingExtraYears,
    upcomingYears: dashboardEChartsStore.activeYears,
  });
  if (missing.length === 0) return;

  const frameworks = [...(dashboardFiltersStore.filters.frameworks ?? [])];

  await Promise.all(
    missing.map(async (year) => {
      store.markExtraPending(year);
      try {
        const reportingPeriod = resolveReportingPeriodForYear(
          year,
          dashboardFiltersStore.available.reportingYears,
        );
        const data = await dashboardService.getEChartsTotals({
          frameworks,
          years: [year],
          ...(reportingPeriod || {}),
        });
        runInAction(() => {
          store.mergeExtraYear(
            year,
            {
              summary: data?.summary?.[String(year)] ?? null,
              warnings: data?.warnings ?? [],
            },
            contextKey,
          );
        });
      } catch {
        // Deliberate no-retry stance: the year stays unloaded (cards keep
        // their dashes, same as a failed range load) and nothing reschedules
        // automatically, so a broken endpoint cannot be hammered. The next
        // user-driven change retriggers the reaction. Swallowing here keeps
        // the reaction free of unhandled rejections.
      } finally {
        runInAction(() => store.clearExtraPending(year, contextKey));
      }
    }),
  );
};
